import { memo } from 'react';
import { IconAuto, IconStop } from '../icons';

export interface AutoButtonProps {
  count: number;
  onClick?: () => void;
  onStop?: () => void;
  size?: number;
  innerSize?: number;
  disabled?: boolean;
}

// Memoized — see SlotUI's `handleOpenAuto`/`handleStopAuto` for the
// matching half of this fix (stable callback identities so this memo
// boundary actually holds during high-frequency parent re-renders like
// swipe-drag frames).
export const AutoButton = memo(function AutoButton({
  count,
  onClick,
  onStop,
  size = 48,
  innerSize,
  disabled = false,
}: AutoButtonProps) {
  const active = count > 0;
  // Stopping an autoplay session must always work, even mid-spin — a
  // player needs to be able to bail out immediately. `disabled` only
  // gates opening the picker (can't change the autospin count while the
  // reels are moving).
  const handle = () => {
    if (active) {
      onStop?.();
      return;
    }
    if (disabled) return;
    onClick?.();
  };
  return (
    <button
      onClick={handle}
      title={active ? 'Stop autospins' : 'Autospins'}
      aria-label={active ? 'Stop autospins' : 'Autospins'}
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        background: '#FE37B4',
        color: '#FFFFFF',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled && !active ? 'not-allowed' : 'pointer',
        position: 'relative',
        padding: 0,
        transition: 'all 0.15s ease',
        outline: 'none',
        opacity: disabled && !active ? 0.4 : 1,
        // Same ring/glow as SpinButton, but the 1px edge uses the button's
        // own pink instead of white — a white ring read too stark against
        // a solid pink fill.
        boxShadow: '0 6px 24px rgba(0,0,0,0.35), 0 0 0 1px #FE37B4, 0 0 0 5px var(--border-2)',
      }}
      onMouseEnter={(e) => {
        if (!disabled || active) e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => (e.currentTarget.style.transform = 'none')}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: active ? 0 : 1,
          transform: active
            ? 'scale(0.7) rotate(-90deg)'
            : 'scale(1) rotate(0deg)',
          transition:
            'opacity 0.22s ease, transform 0.28s cubic-bezier(0.37, 0, 0.63, 1)',
        }}
      >
        <IconAuto size={innerSize ?? (size >= 52 ? 28 : 26)} />
      </div>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: active ? 1 : 0,
          transform: active
            ? 'scale(1) rotate(0deg)'
            : 'scale(0.7) rotate(90deg)',
          transition:
            'opacity 0.22s ease, transform 0.28s cubic-bezier(0.37, 0, 0.63, 1)',
        }}
      >
        <IconStop size={innerSize ?? (size >= 52 ? 22 : 20)} />
      </div>
      <div
        style={{
          position: 'absolute',
          top: -10,
          left: '50%',
          minWidth: 28,
          height: 24,
          padding: '0 8px',
          borderRadius: 12,
          background: 'var(--accent)',
          color: 'var(--accent-ink)',
          fontFamily: 'Montserrat, sans-serif',
          fontSize: 16.8,
          fontWeight: 400,
          letterSpacing: 0.3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          border: '2px solid var(--surface)',
          lineHeight: 1,
          opacity: active ? 1 : 0,
          transform: active
            ? 'translateX(-50%) translateY(0) scale(1)'
            : 'translateX(-50%) translateY(6px) scale(0.6)',
          transformOrigin: 'center bottom',
          transition:
            'opacity 0.22s ease, transform 0.28s cubic-bezier(0.37, 0, 0.63, 1)',
          pointerEvents: 'none',
        }}
      >
        {count || ''}
      </div>
    </button>
  );
});
